import * as AvatarPrimitive from '@radix-ui/react-avatar'
import { cva } from 'class-variance-authority'
import { cn } from 'lib/utils'
import * as React from 'react'

const avatarVariants = cva('relative flex size-10 shrink-0 overflow-hidden rounded-full', {
  variants: {
    size: {
      sm: 'size-8',
      md: 'size-10',
      lg: 'size-24',
    },
  },
  defaultVariants: {
    size: 'md',
  },
})

type AvatarProps = React.ComponentProps<typeof AvatarPrimitive.Root> & {
  size?: 'sm' | 'md' | 'lg'
}

function Avatar({ className, size, ...props }: AvatarProps) {
  return (
    <AvatarPrimitive.Root
      data-slot="avatar"
      className={cn(avatarVariants({ size }), className)}
      {...props}
    />
  )
}

function AvatarImage({ className, ...props }: React.ComponentProps<typeof AvatarPrimitive.Image>) {
  return (
    <AvatarPrimitive.Image
      data-slot="avatar-image"
      className={cn('aspect-square size-full', className)}
      {...props}
    />
  )
}

function AvatarFallback({
  className,
  variant = 'default',
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Fallback> & {
  /** `default` — legacy `bg-muted`. `dark` — T3 dual-theme (paper-faint on bg-3). */
  variant?: 'default' | 'dark'
}) {
  return (
    <AvatarPrimitive.Fallback
      data-slot="avatar-fallback"
      className={cn(
        'flex size-full items-center justify-center rounded-full',
        variant === 'default' && 'bg-muted',
        variant === 'dark' && 'bg-bg-3 text-[12px] font-medium text-paper-dim',
        className
      )}
      {...props}
    />
  )
}

export { Avatar, AvatarFallback, AvatarImage }
